import React, { useEffect, useRef } from "react";
import { Button, Popconfirm, Space, Typography } from "antd";
import { useShiftFocus } from "../PopconfirmShiftFocusContext";
import type { PopconfirmButtonProps } from "./PopconfirmButton.types";
import "./PopconfirmButton.less";
const { Text } = Typography;

const PopconfirmButton = (props: PopconfirmButtonProps): React.JSX.Element => {
    const {
        label,
        confirmText,
        cancelText,
        popconfirmTitleLines,
        onConfirm,
        isDisabled,
        isSubmitting,
        dataTestId = "popconfirm-button",
        openPopconfirm,
        id,
        overlayClassName,
        type,
    } = props;
    const popconfirmRef = useRef<HTMLDivElement>(null);
    const { isClicked, setIsClicked } = useShiftFocus();
    const isOpen = openPopconfirm === id;

    const useAccessiblePopconfirm = (
        isOpen: boolean,
        ariaLabel = "important message",
        additionalClassName = "popconfirm-accessible-overlay"
    ): void => {
        useEffect(() => {
            if (!isOpen) return;

            const interval = setInterval(() => {
                const popover = document.querySelector(
                    `.ant-popover.${additionalClassName}`
                ) as HTMLElement;

                if (popover) {
                    popover.setAttribute("aria-label", ariaLabel);
                    popover.setAttribute("role", "alertdialog");
                    clearInterval(interval);
                }
            }, 100);

            return () => clearInterval(interval);
        }, [isOpen, ariaLabel, additionalClassName]);
    };

    useAccessiblePopconfirm(isOpen);

    const handleButtonClick = (): void => {
        if ("onClick" in props && typeof props.onClick === "function") {
            props.onClick();
        }
        setIsClicked(true);

        if (!isDisabled) {
            props.setOpenPopconfirm?.(id);
        }
    };

    const handleCancel = (): void => {
        setIsClicked(false);
        props.setOpenPopconfirm?.(null);
    };

    const handleConfirm = (): void => {
        onConfirm();
        setIsClicked(false);
        props.setOpenPopconfirm?.(null);
    };

    useEffect(() => {
        const currentPopconfirmRef = popconfirmRef.current;
        if (isClicked && currentPopconfirmRef) {
            const cancelButton = currentPopconfirmRef.querySelector(
                ".cancel-popconfirm-button"
            ) as HTMLButtonElement;
            const okButton = currentPopconfirmRef.querySelector(
                ".ok-popconfirm-button"
            ) as HTMLButtonElement;

            const handleKeyDown = (event: KeyboardEvent): void => {
                if (event.key === "Tab") {
                    event.preventDefault();
                    if (document.activeElement === cancelButton) {
                        okButton?.focus();
                    } else {
                        cancelButton?.focus();
                    }
                }
            };

            currentPopconfirmRef.addEventListener("keydown", handleKeyDown);
            return () =>
                currentPopconfirmRef.removeEventListener(
                    "keydown",
                    handleKeyDown
                );
        }
    }, [isClicked, popconfirmRef, setIsClicked]);

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
            }}
            ref={popconfirmRef}
        >
            <Popconfirm
                title={
                    <Space
                        direction="vertical"
                        size={0}
                        data-testid={`${dataTestId}-popconfirm-title-container`}
                    >
                        {popconfirmTitleLines.map(({ textId, text }) => (
                            <Text
                                key={textId}
                                data-testid={`${dataTestId}-popconfirm-title-text-${textId}`}
                            >
                                {text}
                            </Text>
                        ))}
                    </Space>
                }
                aria-label="important message"
                aria-describedby={`${id}-popconfirm`}
                okText={confirmText}
                cancelText={cancelText}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                disabled={isSubmitting}
                open={isOpen}
                data-testid={`${dataTestId}-popconfirm`}
                cancelButtonProps={{
                    className: "cancel-popconfirm-button",
                }}
                okButtonProps={{
                    className: "ok-popconfirm-button",
                }}
                overlayClassName={`${
                    overlayClassName ?? ""
                } popconfirm-accessible-overlay`}
            >
                <Button
                    type={type}
                    onClick={handleButtonClick}
                    disabled={isSubmitting}
                    data-testid={`${dataTestId}-button`}
                    id={`${id}-popconfirm`}
                >
                    {label}
                </Button>
            </Popconfirm>
        </div>
    );
};

export default PopconfirmButton;
